import { api } from './authService';

export interface DocumentEntry {
  key: string;
  size: number;
  last_modified: string;
  description?: string;
  transaction_date?: string;
  transaction_payee?: string;
  standalone?: boolean;
}

export interface DocumentsResponse {
  documents: DocumentEntry[];
}

export interface UploadResponse {
  key: string;
  doc_type: string;
  description: string;
  filename: string;
  size: number;
}

export async function listDocuments(params?: {
  doc_type?: string;
  year?: number;
}): Promise<DocumentsResponse> {
  const res = await api.get<DocumentsResponse>('/documents', { params });
  return res.data;
}

export async function uploadDocument(
  file: File,
  docType: string,
  description: string,
  transactionDate?: string,
  transactionPayee?: string,
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('doc_type', docType);
  formData.append('description', description);
  if (transactionDate) {
    formData.append('transaction_date', transactionDate);
  }
  if (transactionPayee) {
    formData.append('transaction_payee', transactionPayee);
  }
  const res = await api.post<UploadResponse>('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export interface LinkMultiResult {
  linked: number;
  files: string[];
  missed: { date: string; payee: string }[];
}

export async function linkDocumentToTransactions(
  key: string,
  description: string,
  transactions: { date: string; payee: string }[],
): Promise<LinkMultiResult> {
  const res = await api.post<LinkMultiResult>('/documents/link-multi', {
    key,
    description,
    transactions,
  });
  return res.data;
}

export async function uploadAndLinkMulti(
  file: File,
  docType: string,
  description: string,
  targets: { date: string; payee: string }[],
): Promise<LinkMultiResult | null> {
  if (targets.length === 0) return null;
  const first = targets.find((t) => t.date) || targets[0];
  const upload = await uploadDocument(file, docType, description, first.date, first.payee);
  if (!upload.key) return null;
  return linkDocumentToTransactions(upload.key, description, targets);
}

export async function getTransactionDocuments(
  xactId: string,
): Promise<{ documents: DocumentEntry[] }> {
  const res = await api.get<{ documents: DocumentEntry[] }>(`/transactions/${xactId}/documents`);
  return res.data;
}

export async function downloadDocument(key: string): Promise<Blob> {
  const res = await api.get(`/documents/${key}`, { responseType: 'blob' });
  return res.data;
}

export async function deleteDocument(key: string): Promise<void> {
  await api.delete(`/documents/${key}`);
}
